import { useState } from 'react';
import { Bell, Loader2, AlertCircle, CheckCircle2, XCircle, Circle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import {
  useAssistanceRequests,
  AssistanceReason,
  ASSISTANCE_REASONS,
} from '@/hooks/useAssistanceRequests';
import { useRegisterSession } from '@/hooks/useRegisterSession';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface RequestAssistanceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RequestAssistanceModal({ open, onOpenChange }: RequestAssistanceModalProps) {
  const [selectedReason, setSelectedReason] = useState<AssistanceReason | ''>('');
  const [notes, setNotes] = useState('');
  const { createRequest, myActiveRequest, cancelRequest, getTimeAgo } = useAssistanceRequests();
  const { activeSession } = useRegisterSession();

  const handleSubmitRequest = async () => {
    if (!selectedReason) {
      toast.error('Select a reason', {
        description: 'Please select a reason for your assistance request.',
      });
      return;
    }

    try {
      await createRequest.mutateAsync({
        reason: selectedReason,
        notes: notes.trim() || undefined,
        registerId: activeSession?.register_id,
        sessionId: activeSession?.id,
      });

      toast.success('Assistance requested', {
        description: 'A manager has been notified and will assist you shortly.',
      });

      // Reset form and close modal
      setSelectedReason('');
      setNotes('');
      onOpenChange(false);
    } catch (error: any) {
      toast.error('Failed to request assistance', {
        description: error.message || 'An error occurred. Please try again.',
      });
    }
  };

  const handleCancelRequest = async () => {
    if (!myActiveRequest) return;

    try {
      await cancelRequest.mutateAsync(myActiveRequest.id);
      toast.success('Request cancelled', {
        description: 'Your assistance request has been cancelled.',
      });
      onOpenChange(false);
    } catch (error: any) {
      toast.error('Failed to cancel request', {
        description: error.message || 'An error occurred. Please try again.',
      });
    }
  };

  // If user has an active request, show status instead of form
  if (myActiveRequest) {
    const reasonInfo = ASSISTANCE_REASONS[myActiveRequest.reason];

    return (
      <ResponsiveModal open={open} onOpenChange={onOpenChange}>
        <ResponsiveModalContent maxWidth="sm">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Assistance Request Active</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Your request has been submitted and a manager will assist you soon.
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody className="space-y-4">
            {/* Status indicator */}
            <div
              className={cn(
                'flex items-center gap-3 p-4 rounded-lg border',
                myActiveRequest.status === 'pending'
                  ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800'
                  : 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800'
              )}
            >
              {myActiveRequest.status === 'pending' ? (
                <>
                  <div className="relative">
                    <Bell className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-amber-500 animate-ping" />
                    <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-amber-500" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-amber-800 dark:text-amber-200">
                      Waiting for assistance
                    </p>
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      Submitted {getTimeAgo(myActiveRequest.created_at)}
                    </p>
                  </div>
                  <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                    Pending
                  </Badge>
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  <div className="flex-1">
                    <p className="font-medium text-blue-800 dark:text-blue-200">
                      Help is on the way!
                    </p>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Acknowledged {getTimeAgo(myActiveRequest.acknowledged_at || '')}
                    </p>
                  </div>
                  <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">
                    Acknowledged
                  </Badge>
                </>
              )}
            </div>

            {/* Request details */}
            <div className="space-y-2 p-3 rounded-lg bg-muted/50">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Reason</span>
                <span className="text-sm font-medium">{reasonInfo?.label}</span>
              </div>
              {myActiveRequest.notes && (
                <div>
                  <span className="text-sm text-muted-foreground">Notes</span>
                  <p className="text-sm mt-1">{myActiveRequest.notes}</p>
                </div>
              )}
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            {myActiveRequest.status === 'pending' && (
              <Button
                variant="outline"
                onClick={handleCancelRequest}
                disabled={cancelRequest.isPending}
              >
                {cancelRequest.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  <>
                    <XCircle className="mr-2 h-4 w-4" />
                    Cancel Request
                  </>
                )}
              </Button>
            )}
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>
    );
  }

  // Show request form
  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalContent maxWidth="md">
        <ResponsiveModalHeader>
          <ResponsiveModalTitle>Request Assistance</ResponsiveModalTitle>
          <ResponsiveModalDescription>
            Select a reason and a manager will be notified to assist you.
          </ResponsiveModalDescription>
        </ResponsiveModalHeader>

        <ResponsiveModalBody className="space-y-6">
          {/* Reason selection */}
          <div className="space-y-3">
            <Label>Reason for assistance *</Label>
            <div className="grid gap-2">
              {Object.entries(ASSISTANCE_REASONS).map(([key, { label, description }]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelectedReason(key as AssistanceReason)}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors text-left w-full',
                    selectedReason === key
                      ? 'bg-primary/5 border-primary'
                      : 'hover:bg-muted/50 border-border'
                  )}
                >
                  <div className="mt-0.5">
                    {selectedReason === key ? (
                      <CheckCircle className="h-4 w-4 text-primary" />
                    ) : (
                      <Circle className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{label}</p>
                    <p className="text-sm text-muted-foreground">{description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Notes field */}
          <div className="space-y-2">
            <Label htmlFor="notes">Additional notes (optional)</Label>
            <Textarea
              id="notes"
              placeholder="Add any additional details that might help..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          {/* Warning */}
          <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 border">
            <AlertCircle className="h-4 w-4 mt-0.5 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              This will notify all managers at this location. Please use only when you need
              assistance.
            </p>
          </div>
        </ResponsiveModalBody>

        <ResponsiveModalFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmitRequest}
            disabled={!selectedReason || createRequest.isPending}
          >
            {createRequest.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Requesting...
              </>
            ) : (
              <>
                <Bell className="mr-2 h-4 w-4" />
                Request Assistance
              </>
            )}
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
